import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { ShieldAlert, RefreshCw } from "lucide-react";

export default function GuardianPage() {
  const { lang } = useLang();
  const [h, setH] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [scanning, setScanning] = useState(false);

  const loadAlerts = () =>
    api
      .get("/safety/launch-readiness/alerts?resolved=false")
      .then((r) => setAlerts(r.data.alerts || []))
      .catch(() => setAlerts([]));

  useEffect(() => {
    const load = () => api.get("/guardian/health").then((r) => setH(r.data)).catch(() => {});
    load();
    loadAlerts();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  const runScanNow = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/safety/launch-readiness/run-now");
      toast.success(`Scan complete — ${data.open_alerts} open alert(s)`);
      await loadAlerts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const resolveAlert = async (draftId) => {
    try {
      await api.post(`/safety/launch-readiness/alerts/${draftId}/resolve`);
      setAlerts((prev) => prev.filter((a) => a.draft_id !== draftId));
      toast.success("Alert resolved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resolve");
    }
  };

  return (
    <div className="space-y-6" data-testid="guardian-page">
      <div>
        <h1 className="font-display font-light text-3xl tracking-tight">
          {t(lang, "guardian_title")}
        </h1>
        <p className="mt-1 text-white/50 text-sm">{t(lang, "guardian_subtitle")}</p>
      </div>

      <div className="hq-card p-6 relative overflow-hidden hq-scanline">
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
            System status
          </div>
          <div className="flex items-center gap-2">
            <span className={`hq-dot hq-dot-${h?.overall || "cyan"}`}></span>
            <span className="font-mono text-lg uppercase" data-testid="guardian-overall">
              {h?.overall || "…"}
            </span>
          </div>
        </div>
        <div className="grid gap-1 font-mono text-xs text-white/60">
          <div>host: <span className="text-white/80">{h?.host || "…"}</span></div>
          <div>python: <span className="text-white/80">{h?.python || "…"}</span></div>
          <div>checked_at: <span className="text-white/80">{h?.checked_at || "…"}</span></div>
        </div>
      </div>

      <div className="hq-card overflow-hidden">
        <table className="hq-table">
          <thead>
            <tr>
              <th>{t(lang, "guardian_component")}</th>
              <th>{t(lang, "guardian_status")}</th>
              <th>{t(lang, "guardian_latency")}</th>
              <th>{t(lang, "guardian_message")}</th>
            </tr>
          </thead>
          <tbody>
            {(h?.components || []).map((c) => (
              <tr key={c.id} data-testid={`guardian-row-${c.id}`}>
                <td className="font-mono text-xs">{c.name}</td>
                <td>
                  <span className="inline-flex items-center gap-2">
                    <span className={`hq-dot hq-dot-${c.status}`}></span>
                    <span className="font-mono text-xs uppercase">{c.status}</span>
                  </span>
                </td>
                <td className="font-mono text-xs">
                  {c.latency_ms != null ? `${c.latency_ms}ms` : "—"}
                </td>
                <td className="text-xs text-white/70">{c.message || "—"}</td>
              </tr>
            ))}
            {!h && (
              <tr>
                <td colSpan={4} className="text-center text-white/40 py-8 font-mono text-xs">
                  loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Launch Readiness — daily gate re-check */}
      <div className="hq-card p-6" data-testid="launch-readiness-panel">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-[color:var(--hq-amber)]" />
            <div>
              <div className="font-display text-lg">Launch Readiness</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                Daily 6-question re-check · {alerts.length} open alert(s)
              </div>
            </div>
          </div>
          <button
            onClick={runScanNow}
            disabled={scanning}
            className="hq-btn hq-btn-secondary text-xs disabled:opacity-50"
            data-testid="launch-readiness-scan-now"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${scanning ? "animate-spin" : ""}`} />
            {scanning ? "Scanning…" : "Run scan now"}
          </button>
        </div>

        {alerts.length === 0 ? (
          <div className="text-center text-white/40 py-6 font-mono text-xs">
            All active products passing the 6-question gate.
          </div>
        ) : (
          <table className="hq-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Reasons</th>
                <th>Flagged</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => (
                <tr key={a.draft_id} data-testid={`lr-alert-${a.draft_id}`}>
                  <td className="font-mono text-xs">{a.title_en || a.draft_id}</td>
                  <td className="text-xs text-white/70">{(a.reasons || []).slice(0, 3).join(", ")}</td>
                  <td className="font-mono text-[10px] text-white/50">
                    {a.flagged_at ? new Date(a.flagged_at).toLocaleString() : "—"}
                  </td>
                  <td>
                    <button
                      onClick={() => resolveAlert(a.draft_id)}
                      className="hq-btn hq-btn-ghost text-xs"
                      data-testid={`lr-resolve-${a.draft_id}`}
                    >
                      Resolve
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
